// <DomainGlyph> — registered SVG glyph from
// design/20-design/visual-graphics-designer/graphics-spec.md §16.
//
// Registry-restricted: unknown names are rejected at runtime with a console
// warning so a missing glyph cannot ship silently. Every glyph is a token-
// driven inline SVG that reads `currentColor` and platform tokens via
// `var(--md-…)`; consumers never inline `<svg fill="#hex">`.
//
// Adding a new glyph = update graphics-spec.md §16, supervisor approves the
// stable name, then add a registry entry below.

import type { CSSProperties, ReactNode } from 'react';

export const DOMAIN_GLYPH_NAMES = [
  'gv-confidence-pin-high',
  'gv-confidence-pin-medium',
  'gv-confidence-pin-low',
  'gv-confidence-pin-unknown',
  'gv-defence-local-only',
  'gv-pcd-density-ramp',
  'gv-pcd-classification-ramp',
  'gv-pcd-elevation-ramp',
  'gv-anomaly-marker',
  'gv-scenario-legend-chip-a',
  'gv-scenario-legend-chip-b',
  'gv-diff-badge-added',
  'gv-diff-badge-removed',
  'gv-diff-badge-changed',
  'gv-diff-badge-unchanged',
  'gv-lifecycle-dot-approved',
  'gv-lifecycle-dot-rejected',
  'gv-lifecycle-dot-locked',
  'gv-lifecycle-dot-pending',
] as const;

export type DomainGlyphName = (typeof DOMAIN_GLYPH_NAMES)[number];

interface GlyphEntry {
  defaultLabel: string;
  render(size: number): ReactNode;
}

// All glyphs are stub implementations sized 16×16 viewBox; visual designer
// will refine in graphics-spec.md §16 final pass. Tokens via `currentColor`
// or `var(--md-…)`. No raw `#hex`.
function circleGlyph(size: number, fillVar: string, strokeVar?: string) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <circle
        cx="8"
        cy="8"
        r="6"
        fill={fillVar}
        stroke={strokeVar ?? 'transparent'}
        strokeWidth="1"
      />
    </svg>
  );
}

function rampGlyph(size: number) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <defs>
        <linearGradient id="ramp" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="var(--md-info, currentColor)" />
          <stop offset="100%" stopColor="var(--md-warning, currentColor)" />
        </linearGradient>
      </defs>
      <rect x="1" y="6" width="14" height="4" fill="url(#ramp)" rx="2" />
    </svg>
  );
}

function diamondGlyph(size: number) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1 L15 8 L8 15 L1 8 Z"
        fill="currentColor"
        stroke="var(--md-outline, currentColor)"
      />
    </svg>
  );
}

function chipGlyph(size: number, fill: string) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2" y="5" width="12" height="6" rx="3" fill={fill} />
    </svg>
  );
}

function diffGlyph(size: number, label: string) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1" y="1" width="14" height="14" rx="2" fill="transparent" stroke="currentColor" />
      <text
        x="8"
        y="11"
        textAnchor="middle"
        fontSize="10"
        fontFamily="ui-monospace, monospace"
        fill="currentColor"
      >
        {label}
      </text>
    </svg>
  );
}

const REGISTRY: Readonly<Record<DomainGlyphName, GlyphEntry>> = {
  'gv-confidence-pin-high': {
    defaultLabel: 'High confidence',
    render: (s) => circleGlyph(s, 'var(--md-success, currentColor)'),
  },
  'gv-confidence-pin-medium': {
    defaultLabel: 'Medium confidence',
    render: (s) => circleGlyph(s, 'var(--md-warning, currentColor)'),
  },
  'gv-confidence-pin-low': {
    defaultLabel: 'Low confidence',
    render: (s) => circleGlyph(s, 'var(--md-error, currentColor)'),
  },
  'gv-confidence-pin-unknown': {
    defaultLabel: 'Unknown confidence',
    render: (s) => circleGlyph(s, 'transparent', 'currentColor'),
  },
  'gv-defence-local-only': {
    defaultLabel: 'Local-only (defence variant)',
    render: (s) => (
      <svg width={s} height={s} viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M8 1 L14 4 V8 C14 11 11 14 8 15 C5 14 2 11 2 8 V4 Z"
          fill="transparent"
          stroke="currentColor"
        />
      </svg>
    ),
  },
  'gv-pcd-density-ramp': { defaultLabel: 'Density ramp', render: rampGlyph },
  'gv-pcd-classification-ramp': { defaultLabel: 'Classification ramp', render: rampGlyph },
  'gv-pcd-elevation-ramp': { defaultLabel: 'Elevation ramp', render: rampGlyph },
  'gv-anomaly-marker': {
    defaultLabel: 'Anomaly marker',
    render: diamondGlyph,
  },
  'gv-scenario-legend-chip-a': {
    defaultLabel: 'Scenario A',
    render: (s) => chipGlyph(s, 'var(--md-info, currentColor)'),
  },
  'gv-scenario-legend-chip-b': {
    defaultLabel: 'Scenario B',
    render: (s) => chipGlyph(s, 'var(--md-tertiary, currentColor)'),
  },
  'gv-diff-badge-added': { defaultLabel: 'Added', render: (s) => diffGlyph(s, '+') },
  'gv-diff-badge-removed': { defaultLabel: 'Removed', render: (s) => diffGlyph(s, '−') },
  'gv-diff-badge-changed': { defaultLabel: 'Changed', render: (s) => diffGlyph(s, '~') },
  'gv-diff-badge-unchanged': { defaultLabel: 'Unchanged', render: (s) => diffGlyph(s, '=') },
  'gv-lifecycle-dot-approved': {
    defaultLabel: 'Approved',
    render: (s) => circleGlyph(s, 'var(--md-success, currentColor)'),
  },
  'gv-lifecycle-dot-rejected': {
    defaultLabel: 'Rejected',
    render: (s) => circleGlyph(s, 'var(--md-error, currentColor)'),
  },
  'gv-lifecycle-dot-locked': {
    defaultLabel: 'Locked',
    render: (s) => circleGlyph(s, 'var(--md-sil-purple, currentColor)'),
  },
  'gv-lifecycle-dot-pending': {
    defaultLabel: 'Pending',
    render: (s) => circleGlyph(s, 'var(--md-on-surface-variant, currentColor)'),
  },
};

export const DOMAIN_GLYPH_REGISTRY: Readonly<Record<DomainGlyphName, { defaultLabel: string }>> =
  Object.fromEntries(
    DOMAIN_GLYPH_NAMES.map((n) => [n, { defaultLabel: REGISTRY[n].defaultLabel }]),
  ) as Readonly<Record<DomainGlyphName, { defaultLabel: string }>>;

export interface DomainGlyphProps {
  name: DomainGlyphName;
  size?: 12 | 16 | 20 | 24 | 28 | 32;
  /** Required when there is no visible label. */
  'aria-label'?: string;
  /** When true, decorative — sets `aria-hidden`. */
  decorative?: boolean;
  style?: CSSProperties;
  className?: string;
  'data-testid'?: string;
}

export function DomainGlyph({
  name,
  size = 16,
  'aria-label': ariaLabel,
  decorative = false,
  style,
  className,
  'data-testid': testId,
}: DomainGlyphProps) {
  const entry = REGISTRY[name];
  if (!entry) {
    if (typeof console !== 'undefined') {
      console.error(
        `[geosoft] <DomainGlyph name="${name}"> not in registry. ` +
          `Add to graphics-spec.md §16 + DomainGlyph.tsx registry first.`,
      );
    }
    return null;
  }
  const label = decorative ? undefined : (ariaLabel ?? entry.defaultLabel);
  return (
    <span
      role={decorative ? 'presentation' : 'img'}
      aria-label={label}
      aria-hidden={decorative ? true : undefined}
      data-testid={testId}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        color: 'currentColor',
        flex: '0 0 auto',
        ...style,
      }}
    >
      {entry.render(size)}
    </span>
  );
}
